<template>
  <div>
    <Head :title="__(title)" />

    <div class="bg-white rounded-xl shadow-sm border border-gray-100 mb-4 ticket-filters-card">
      <div class="flex flex-col md:flex-row gap-3 p-4 justify-between items-center ticket-filters">
        <search-input v-model="form.search" placeholder="Search by Key, Subject, Priority, Status, Assign to..." class="w-full max-w-md search" @reset="reset" />
        <div class="filter-add-new flex flex-col gap-3 md:flex-row items-center">
          <select v-model.number="form.per_page" class="ticket-filter-select">
            <option :value="10">10 / {{ __('page') }}</option>
            <option :value="25">25 / {{ __('page') }}</option>
            <option :value="50">50 / {{ __('page') }}</option>
            <option :value="100">100 / {{ __('page') }}</option>
          </select>
          <Link class="btn-indigo" :href="route('tickets.create')">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 inline-block flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
            </svg>
            <span>{{ __('New Ticket') }}</span>
          </Link>
        </div>
      </div>

      <div class="filters-toggle flex items-center justify-between px-4 py-3 border-t" @click="showFilters = !showFilters">
        <span class="text-sm font-medium flex items-center gap-2">
          {{ activeFiltersCount }} {{ __('filters active') }}
        </span>
        <icon name="cheveron-down" class="w-4 h-4 filters-toggle-icon" :class="{ 'is-open': showFilters }" />
      </div>

      <transition name="accordion">
        <div v-show="showFilters" class="filters-body px-4 pb-4 border-t">
          <div class="flex flex-wrap gap-2 pt-3">
            <button
              v-for="badge in quickFilters"
              :key="badge.value"
              type="button"
              class="quick-badge"
              :class="[`quick-badge--${badge.value}`, { 'quick-badge--active': form.quick_filter === badge.value }]"
              @click="toggleQuickFilter(badge.value)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="quick-badge__icon">
                <path stroke-linecap="round" stroke-linejoin="round" :d="badge.icon" />
              </svg>
              <span>{{ badge.label }}</span>
              <span class="quick-badge__count">{{ badge_counts[badge.value] ?? 0 }}</span>
            </button>
          </div>

          <div class="flex flex-wrap gap-2 items-center pt-3">
            <label class="font-semibold whitespace-nowrap text-sm">{{ __('Filter By Date') }} :</label>
            <select v-model="form.date_type" class="ticket-filter-select">
              <option value="" selected>-- {{ __('Pilih Filter') }} --</option>
              <option value="created_at">{{ __('Created') }}</option>
              <option value="updated_at">{{ __('Updated') }}</option>
              <option value="deadline">{{ __('Deadline') }}</option>
            </select>
            <input v-model="form.date_from" type="date" class="ticket-filter-input" @focus="$event.target.showPicker?.()" @mousedown="onDateMousedown($event)" />
            <input v-model="form.date_to" type="date" class="ticket-filter-input" @focus="$event.target.showPicker?.()" @mousedown="onDateMousedown($event)" />
          </div>

          <div class="flex flex-wrap gap-2 items-center pt-3">
            <span class="text-sm font-medium whitespace-nowrap">{{ __('Filter Ticket By') }}:</span>

            <select-input-filter
              v-if="auth.user.role.slug != 'customer'"
              v-model="form.customer_id"
              :placeholder="__('Requester')"
              :on-input="doFilterRequesters"
              :items="requesters"
              class="ticket-filter-item"
              @focus="doFilterRequesters"
            >
              <option :value="null">{{ __('Assign To') }}</option>
              <option v-for="s in requesters" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select-input-filter>

            <select-input v-if="!(hidden_fields && hidden_fields.includes('ticket_type'))" v-model="form.type_id" class="ticket-filter-item">
              <option :value="null">{{ __('Type') }}</option>
              <option v-for="s in types" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select-input>

            <select-input-filter
              v-if="!(hidden_fields && hidden_fields.includes('category'))"
              v-model="form.category_id"
              :placeholder="__('Category')"
              :on-input="doFilterCategoriesDepartment"
              :items="categoriesDepartment"
              class="ticket-filter-item"
              @focus="doFilterCategoriesDepartment"
            >
              <option :value="null">{{ __('Category') }}</option>
              <option v-for="s in categoriesDepartment" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select-input-filter>

            <select-input
              v-if="['admin', 'super_admin'].includes(auth.user.role.slug)"
              v-model="form.department_id"
              class="ticket-filter-item"
            >
              <option :value="null">{{ __('Department') }}</option>
              <option v-for="s in departments" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select-input>

            <select-input v-model="form.priority_id" class="ticket-filter-item">
              <option :value="null">{{ __('Priority') }}</option>
              <option v-for="s in priorities" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select-input>

            <select-input-filter
              v-if="!(hidden_fields && hidden_fields.includes('status'))"
              v-model="form.status_id"
              :placeholder="__('Status')"
              :on-input="doFilterStatusesDepartment"
              :items="statusesDepartment"
              class="ticket-filter-item"
              @focus="doFilterStatusesDepartment"
            >
              <option :value="null">{{ __('Status') }}</option>
              <option v-for="s in statusesDepartment" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select-input-filter>

            <select-input-filter
              v-if="!(hidden_fields && hidden_fields.includes('assigned_to')) && auth.user.role.slug !== 'agency'"
              v-model="form.assigned_to"
              :placeholder="__('Assign To')"
              :on-input="doFilter"
              :items="assignees"
              class="ticket-filter-item"
              @focus="doFilter"
            >
              <option :value="null">{{ __('Assign To') }}</option>
              <option v-for="s in assignees" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select-input-filter>
          </div>
        </div>
      </transition>
    </div>

    <!-- Mobile card list -->
    <div class="ticket-cards md:hidden flex flex-col gap-2.5">
      <Link
        v-for="ticket in tickets.data"
        :key="ticket.id"
        :href="route('tickets.edit', ticket.uid || ticket.id)"
        :class="['ticket-card', getDeadlineLevel(ticket.deadline, ticket.status === 'Closed' || ticket.status == 'Rejected')]"
      >
        <div class="ticket-card__top">
          <span class="ticket-card__uid">#{{ ticket.uid || ticket.id }}</span>
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset ring-black/10"
            :class="{
              'bg-white text-gray-500': ticket.status === 'Closed',
              'bg-yellow-400 text-yellow-900': ticket.status === 'Pending',
              'bg-green-900 text-white': ticket.status === 'Approved',
              'bg-red-900 text-white': ticket.status === 'Rejected',
              'bg-blue-900 text-white': !['Closed', 'Pending', 'Approved', 'Rejected'].includes(ticket.status)
            }"
          >
            {{ ticket.status }}
          </span>
        </div>
        <div class="ticket-card__subject" :title="ticket.subject">{{ ticket.subject }}</div>
        <div class="ticket-card__meta">
          <span class="ticket-card__meta-item">
            <icon name="check" class="flex-shrink-0 h-3.5 w-3.5 fill-green-600 mr-1" />
            {{ properName(ticket.requester) }} → {{ properName(ticket.assigned_to) }}
          </span>
          <span class="ticket-card__meta-item">
            <icon name="fa-file" class="flex-shrink-0 h-3 fill-gray-400 mr-1" />
            {{ ticket.category }}
          </span>
        </div>
        <div class="ticket-card__footer">
          <span class="ticket-card__footer-item">
            <icon name="time" class="flex-shrink-0 h-3 fill-gray-400 mr-1" />
            {{ __('Created') }} {{ moment(ticket.created_at).fromNow() }}
          </span>
          <span class="ticket-card__footer-item">
            <icon name="time" class="flex-shrink-0 h-3 fill-gray-400 mr-1" />
            {{ ticket.deadline ? __('Deadline') + ' ' + moment(ticket.deadline).fromNow() : __('No Deadline') }}
          </span>
        </div>
      </Link>
      <div v-if="tickets.data.length === 0" class="ticket-card ticket-card--empty">{{ __('No ticket found.') }}</div>
    </div>

    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto hidden md:block">
      <table class="min-w-full whitespace-nowrap ticket_list">
        <tr class="text-left font-bold">
          <th v-for="(h, i) in headers" :key="i">
            <span :class="{'sort': h.sort, 'active' : form.field === h.name},form.direction">{{ __(h.name) }}
              <span v-if="h.sort" class="icons">
                <icon class="fill-gray-300" :class="{'fill-gray-800': (form.direction === 'desc' && form.field === h.value)}" name="up" @click="sort(h.value)" />
                <icon class="fill-gray-300" :class="{'fill-gray-800': form.direction === 'asc' && form.field === h.value}" name="down" @click="sort(h.value)" />
              </span>
            </span>
          </th>
        </tr>
        <tr
          v-for="ticket in tickets.data"
          :key="ticket.id"
          :class="[
            'hover:bg-gray-100 focus-within:bg-gray-100 ticket-row',
            getDeadlineLevel(ticket.deadline, ticket.status === 'Closed' || ticket.status == 'Rejected')
          ]"
        >
          <td class="border-t">
            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="route('tickets.edit', ticket.uid || ticket.id)">
              #{{ ticket.uid || ticket.d }}
            </Link>
          </td>
          <td class="border-t">
            <span class="s__details flex flex-col">
              <span class="subject_t">{{ properName(ticket.requester) }}</span>
              <span class="user__d flex text-xs items-center pt-1">
                <span class="user__c flex items-center" :title="__('Assigned To')">
                  <icon name="check" class="flex-shrink-0 h-3.5 w-3.5 fill-green-600 mr-1" />
                  {{ properName(ticket.assigned_to) }}
                </span>
              </span>
            </span>
          </td>
          <td class="border-t">
            <span class="s__details flex flex-col">
              <span
                class="subject_t font-semibold"
                :title="ticket.subject"
              >
                {{ ticket.subject.length > 50 ? ticket.subject.slice(0, 50) + '...' : ticket.subject }}
              </span>
              <span class="user__d flex text-xs items-center pt-1">

                <span class="user__c flex items-center">
                  <icon name="fa-file" class="flex-shrink-0 h-3 fill-gray-400 pr-1" />
                  Category : {{ ticket.category }}
                </span>
              </span>
            </span>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="route('tickets.edit', ticket.uid || ticket.id)">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset ring-black/10"
                :class="{
                  'bg-white text-gray-500': ticket.status === 'Closed',
                  'bg-yellow-400 text-yellow-900': ticket.status === 'Pending',
                  'bg-green-900 text-white': ticket.status === 'Approved',
                  'bg-red-900 text-white': ticket.status === 'Rejected',
                  'bg-blue-900 text-white': !['Closed', 'Pending', 'Approved', 'Rejected'].includes(ticket.status)
                }"
              >
                {{ ticket.status }}
              </span>
            </Link>
          </td>
          <td class="border-t">
            <span class="s__details flex flex-col">
              <span class="subject_t">{{ moment(ticket.created_at).format("DD/MM/YYYY") }}</span>
              <span class="user__d flex text-xs items-center pt-1">
                <span class="user__n flex items-center pr-4">
                  <icon name="time" class="flex-shrink-0 h-3 fill-gray-400 pr-1" />
                  {{ moment(ticket.created_at).fromNow() }}
                </span>
              </span>
            </span>
          </td>
          <td class="border-t">
            <span class="subject_t">{{ moment(ticket.updated_at).format("DD/MM/YYYY") }}</span>
            <span class="user__d flex text-xs items-center pt-1">
              <span class="user__n flex items-center pr-4">
                <icon name="time" class="flex-shrink-0 h-3 fill-gray-400 pr-1" />
                {{ moment(ticket.updated_at).fromNow() }}
              </span>
            </span>
          </td>
          <td class="border-t">
            <span class="s__details flex flex-col">
              <span class="subject_t">
                {{ ticket.deadline ? moment(ticket.deadline).format("DD/MM/YYYY") : "No Deadline" }}
              </span>
              <span class="user__d flex text-xs items-center pt-1">
                <span class="user__n flex items-center pr-4">
                  <icon name="time" class="flex-shrink-0 h-3 fill-gray-400 pr-1" />
                  {{ ticket.deadline ? moment(ticket.deadline).fromNow() : "No Deadline" }}
                </span>
              </span>
            </span>
          </td>
        </tr>
        <tr v-if="tickets.data.length === 0">
          <td class="border-t px-6 py-4" colspan="9">{{ __('No ticket found.') }}</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-4" :links="tickets.links" />
  </div>
</template>

<style>
.ticket-card {
    display: block;
    background: #fff;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 12px 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.ticket-card.critical {
    background-color: rgba(239, 68, 68, 0.08);
    border-left: 3px solid #ef4444;
}
.ticket-card.warning {
    background-color: rgba(234, 179, 8, 0.08);
    border-left: 3px solid #eab308;
}
.ticket-card__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
}
.ticket-card__uid {
    font-weight: 700;
    font-size: 13px;
    color: #15803d;
}
.ticket-card__subject {
    font-weight: 600;
    font-size: 14px;
    color: #1e293b;
    margin-bottom: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}
.ticket-card__meta {
    display: flex;
    flex-direction: column;
    gap: 3px;
    margin-bottom: 8px;
}
.ticket-card__meta-item,
.ticket-card__footer-item {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #64748b;
}
.ticket-card__footer {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding-top: 8px;
    border-top: 1px solid #f1f5f9;
}
.ticket-card--empty {
    text-align: center;
    color: #64748b;
    font-size: 13px;
}

/* ── Dark mode ───────────────────────────────────── */
.dark .ticket-card {
    background: #1a1a1a;
    border-color: #2d3748;
}
.dark .ticket-card__subject {
    color: #f1f5f9;
}
.dark .ticket-card__footer {
    border-top-color: #2d3748;
}
.dark .ticket-card--empty {
    color: #94a3b8;
}
</style>


<script>
import { Link, Head } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
// import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SelectInput from '@/Shared/SelectInput'
import SearchInput from '@/Shared/SearchInput'
import SelectInputFilter from '@/Shared/SelectInputFilter'
import moment from 'moment'
import axios from 'axios'

export default {
    metaInfo: { title: 'Tickets' },
    components: {
        SearchInput,
        Icon,
        Link,
        Head,
        Pagination,
        SelectInputFilter,
        SelectInput,
    },
    layout: Layout,
    props: {
        filters: Object,
        tickets: Object,
        auth: Object,
        title: String,
        roles: Array,
        priorities: Array,
        statuses: Array,
        types: Array,
        categories: Array,
        departments: Array,
        hidden_fields: Object,
        badge_counts: Object,
    },
    remember: 'form',
    data() {
        return {
            showFilters: true,
            categoriesDepartment: [],
            statusesDepartment: [],
            requesters: [],
            assignees: [],
            headers: [
                {name: 'Kode Tiket', value: 'uid', sort: true},
                {name: 'Requester', value: 'user_id', sort: true},
                {name: 'Subject', value: 'subject', sort: true},
                {name: 'Status', value: 'status_id', sort: true},
                {name: 'Date', value: 'created_at', sort: true},
                {name: 'Updated', value: 'updated_at', sort: true},
                {name: 'Deadline', value: 'deadline', sort: true},
            ],
            user_access: this.$page.props.auth.user.access,
            quickFilters: [
                {
                    label: 'Pending',
                    value: 'pending',
                    icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
                },
                {
                    label: 'Process',
                    value: 'process',
                    icon: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                },
                {
                    label: 'Deadline',
                    value: 'deadline',
                    icon: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zM12 15.75h.008v.008H12v-.008z',
                },
                {
                    label: 'Unclosed',
                    value: 'unclosed',
                    icon: 'M3.75 9h16.5m-16.5 6.75h16.5',
                },
            ],
            form: {
                search: this.filters.search,
                customer_id: this.filters.customer_id,
                field: this.filters.field,
                direction: this.filters.direction,
                priority_id: this.filters.priority_id ?? null,
                status_id: this.filters.status_id ?? null,
                type_id: this.filters.type_id ?? null,
                category_id: this.filters.category_id ?? null,
                department_id: this.filters.department_id ?? null,
                requester_id: this.filters.requester_id ?? null,
                assignees: null,
                requesters: null,
                date_from: this.filters.date_from ?? null,
                date_to: this.filters.date_to ?? null,
                date_type: this.filters.date_type ?? '',
                type: this.filters.type ?? null,
                per_page: this.filters.per_page ? parseInt(this.filters.per_page) : 10,
                quick_filter: this.filters.quick_filter ?? null,
            },
        }
    },
    computed: {
        activeFiltersCount() {
            const keys = ['customer_id', 'type_id', 'category_id', 'department_id', 'priority_id', 'status_id', 'assigned_to', 'date_from', 'date_to']
            return keys.filter((key) => this.form[key] !== null && this.form[key] !== '' && this.form[key] !== undefined).length
        },
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get(this.route('tickets'), pickBy(this.form), { replace: true, preserveState: true })
            }, 150),
        },
    },
    created() {
        this.moment = moment
    },
    methods: {
        doFilter(e) {
            const searchTerm = e.target.value

            axios.get(this.route('filter.assignees', { search: searchTerm }))
                .then((response) => {

                    if (Array.isArray(response.data)) {
                        this.assignees.splice(0, this.assignees.length, ...response.data)
                    } else {
}
                })
                .catch((error) => {
})
        },
        doFilterRequesters(e) {
            const searchTerm = e.target.value

            axios.get(this.route('filter.requesters', { search: searchTerm }))
                .then((response) => {
                    if (Array.isArray(response.data)) {
                        this.requesters.splice(0, this.requesters.length, ...response.data)
                    } else {
}
                })
                .catch((error) => {
})
        },
        doFilterCategoriesDepartment(e) {
            const searchTerm = e.target.value

            axios.get(this.route('filter.categoriesdept', { search: searchTerm }))
                .then((response) => {
                    if (Array.isArray(response.data)) {
                        this.categoriesDepartment.splice(0, this.categoriesDepartment.length, ...response.data)
                    } else {
}
                })
                .catch((error) => {
})
        },
        doFilterStatusesDepartment(e) {
            const searchTerm = e.target.value

            axios.get(this.route('filter.statuses', { search: searchTerm }))
                .then((response) => {
                    if (Array.isArray(response.data)) {
                        this.statusesDepartment.splice(0, this.statusesDepartment.length, ...response.data)
                    } else {
}
                })
                .catch((error) => {
})
        },
        toggleQuickFilter(value) {
            this.form.quick_filter = this.form.quick_filter === value ? null : value
            // reset status_id agar tidak konflik
            this.form.status_id = null
        },
        onDateMousedown(e) {
            const input = e.target
            if (document.activeElement !== input) return
            requestAnimationFrame(() => {
                if (document.activeElement === input) input.blur()
            })
        },
        sort(field) {
            this.form.field = field
            this.form.direction = this.form.direction === 'asc' ? 'desc' : 'asc'
        },
        reset() {
            this.form.search = ''
            this.form.requester_id = null
            this.form.category_id = null
            this.form.status_id = null
            this.form.date_from = null
            this.form.date_to = null
            this.form.date_type = null
            this.form.type = null
        },
        getDeadlineLevel(deadline, isClosed) {
            if (!deadline) return ''

            // Jika ticket sudah closed → selalu normal
            if (isClosed) {
                return 'normal'
            }

            const now = new Date()
            const end = new Date(deadline)
            const diff = (end - now) / (1000 * 60 * 60 * 24)

            // <= 3 hari → Critical
            if (diff <= 3) {
                return 'critical'
            }

            // 4 – 8 hari → Warning
            if (diff > 3 && diff <= 8) {
                return 'warning'
            }

            // > 8 hari → Normal
            return 'normal'
        },
    },
}
</script>
